<template>
  <v-row>
    <v-col md="12">
      <v-card>
        <v-card-title>
          <v-row no-gutters>
            <span class="mt-3">Notification List</span>
            <v-divider class="mx-4" inset vertical></v-divider>
            <v-switch label="Unread" v-model="isRead" color="success"></v-switch>
            <v-spacer></v-spacer>
          </v-row>
        </v-card-title>
        <v-card-text class="notif-card-text">
          <v-data-table-server
            :items="notifications"
            :headers="headers"
            :loading="loading"
            :items-per-page="numberOfPages"
            @update:options="(event: any) => tableOptions = event"
            :items-length="totalItemsCount"
          >
            <template v-slot:item.IsNew="{ item }">
              <notification-icon-content :item="item" class="inner-icon" />
            </template>
            <template v-slot:item.ShortDesc="{ item }">
              {{ item.ShortDesc }}
            </template>
            <template v-slot:item.Text="{ item }">
              <p class="notification-description">
                {{ item.Text }}
              </p>
            </template>
            <template v-slot:item.Info="{ item }">
              <notification-owner-content :item="item" :userId="userId" :isOwner="isNotificationCreator(item)" />
            </template>
            <template v-slot:item.Actions="{ item }">
              <notification-redirect-content :item="item" :userId="userId" :isTeamMember="isTeamMember" :isAdmin="isAdmin" :isClient="isClient" />
              <v-icon icon fab x-small title="View Notification" :class="{ 'not-seen': !isSeen(item) }" @click="showDetail(item)"
                >mdi-eye-outline</v-icon
              >
            </template>
          </v-data-table-server>
        </v-card-text>
      </v-card>
    </v-col>
    <v-dialog
      @click:outside="clickedOutside()"
      v-if="showDetailPopUp"
      v-model="showDetailPopUp"
      persistent
      width="800px"
      @keydown.esc="showDetailPopUp = false"
    >
      <notification-detail
        :userId="userId"
        :isClient="isClient"
        :isAdmin="isAdmin"
        :isTeamMember="isTeamMember"
        @makeSeen="makeSeen"
        @initNotifications="initNotifications"
        :notificationItem="selectedItem"
        @cancel="showDetailPopUp = false"
      ></notification-detail>
    </v-dialog>
  </v-row>
</template>
<script lang="ts">
import NotificationDetail from "./NotificationDetail.vue";
import NotificationItem from "../../models/NotificationItem";
import { defineComponent } from "vue";
import NotificationService from "../../services/NotificationService";
import NotificationIconContent from "./NotificationIconContent.vue";
import NotificationRedirectContent from "./NotificationRedirectContent.vue";
import NotificationOwnerContent from "./NotificationOwnerContent.vue";

export default defineComponent({
  components: {
    NotificationDetail,
    NotificationIconContent,
    NotificationRedirectContent,
    NotificationOwnerContent,
  },
  props: {
    userId: {
      type: String,
    },
    isClient: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    isTeamMember: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      notifications: [] as NotificationItem[],
      loading: true,
      showDetailPopUp: false,
      isRead: false,
      selectedItem: {} as NotificationItem,
      headers: [
        { title: "", sortable: false, value: "IsNew", align: "start" },
        { title: "", sortable: false, value: "Type" },
        { title: "Short Description", value: "ShortDesc", sortable: false },
        { title: "Text", align: "start", value: "Text" },
        { title: "Info", value: "Info" },
        { title: "Actions", sortable: false, value: "Actions" },
      ],
      page: 1,
      numberOfPages: 10,
      sortProp: "CreatedAt",
      sortOrder: "desc",
      tableOptions: {} as any,
      totalItemsCount: 0,
    };
  },
  methods: {
    isSeen(item: any) {
      if (this.notificationItemNotSeenRepliesCount(item) > 0) {
        return false;
      }
      return item.Status === "Seen" || this.isNotificationCreator(item);
    },
    notificationItemNotSeenRepliesCount(item: any) {
      let notSeenReplies = 0;
      item.Replies.forEach((reply: any) => {
        if (!this.isReplySeen(item, reply)) {
          notSeenReplies++;
        }
      });
      return notSeenReplies;
    },
    isReplySeen(notificationItem: any, reply: any) {
      return reply.Status === "Seen" || reply.SentByCreator === this.isNotificationCreator(notificationItem);
    },
    isNotificationCreator(item: any) {
      return item.CreatorUserId === this.userId;
    },
    clickedOutside() {
      this.showDetailPopUp = false;
    },
    async makeSeen($model: any) {
      if ($model.Status !== "Seen") {
        $model.Status = "Seen";
        $model.SeenDate = new Date();
        await NotificationService.update($model.id, $model.Status, $model.SeenDate, this.isClient);
      }
    },
    showDetail(item: any) {
      this.selectedItem = item;
      this.showDetailPopUp = true;
    },

    async initNotifications() {
      try {
        this.loading = true;
        this.showDetailPopUp = false;
        const { page, itemsPerPage } = this.tableOptions;
        const result = await NotificationService.getNotificationItems(page, itemsPerPage, this.isClient, this.isRead, this.sortProp, this.sortOrder);

        this.notifications = result.data;
        this.totalItemsCount = result.totalCount;
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    isRead(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.initNotifications();
      }
    },
    tableOptions(newVal, oldValue) {
      if (oldValue && newVal != oldValue) {
        if (newVal.sortBy && newVal.sortBy.length) {
          this.sortProp = newVal.sortBy[0].key;
        }

        if (newVal.sortDesc && newVal.sortDesc.length) {
          this.sortOrder = newVal.sortDesc[0] ? "desc" : "asc";
        }
        this.initNotifications();
      }
    },
  },
});
</script>
<style scoped lang="scss">
@import "node_modules/shared-components/assets/colors.scss";
.not-seen {
  color: $racecar;
}
.notification-description {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 580px;
}
@media (max-width: 1310px) {
  .notification-description {
    width: 180px;
  }
}
@media (max-width: 370px) {
  .notif-card-text {
    padding-top: 50px;
  }
}
</style>
